import { useState, useEffect, useMemo } from 'react';
import { FaUsers, FaSpinner } from 'react-icons/fa';
import { motion } from 'framer-motion';
import dayjs from 'dayjs';
import utc from "dayjs/plugin/utc";
import timezone from 'dayjs/plugin/timezone';

import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchTimeSlotsRequest } from '../redux/slices/timeSlotsSlice';
import { makeReservationRequest, resetReservationState } from '../redux/slices/makeReservationSlice';
import { RootState } from '../redux/store';

import { ReservationSuccessPopup, ReservationFailurePopup, TimeSlotSelector } from '../components';
import { useNavSpacing } from '../hooks/useNavSpacing';

import { checkIfRestaurantClosed } from '../utils/reservationUtils';
import { filterTimeSlots } from '../utils/timeSlotFilters';


dayjs.extend(utc);
dayjs.extend(timezone);


const DEFAULT_TIMEZONE = "America/New_York";


type SlotStatus =
  | "loading"
  | "closed"
  | "past"
  | "no-working-hours"
  | "no-slots"
  | "available";


export default function TableReservationsPage() {

  const dispatch = useAppDispatch();
  const navSpacing = useNavSpacing();

  const selectedRestaurant =
    useAppSelector(
      (state: RootState) =>
        state.restaurant.selectedRestaurant
    );

  const timezone =
    selectedRestaurant?.restaurantDetails?.timeZone ||
    (selectedRestaurant as any)?.timeZone ||
    DEFAULT_TIMEZONE;

  const getTodayDate = () => {
    return dayjs()
      .tz(timezone)
      .format("YYYY-MM-DD");
  };


  const [selectedDate, setSelectedDate] = useState(
    () => getTodayDate()
  );

  const [selectedTime, setSelectedTime] = useState('');

  const [name, setName] = useState('');
  const [partySize, setPartySize] = useState(1);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [specialRequest, setSpecialRequest] = useState('');


  const {
    timeSlots: apiTimeSlots,
    loading: tableLoading,
    error: timeSlotsError
  } = useAppSelector(
    (state: RootState) => state.timeSlots
  );


  const {
    loading: reservationLoading,
    success: reservationSuccess,
    reservationData,
    error: reservationError
  } = useAppSelector(
    (state: RootState) => state.makeReservation
  );





  const [timeSlots, setTimeSlots] =
    useState<string[]>([]);


  const [showSuccessPopup, setShowSuccessPopup] =
    useState(false);

  const [showFailurePopup, setShowFailurePopup] =
    useState(false);
  const [isRestaurantClosed, setIsRestaurantClosed] =
    useState(false);

  const resetForm = () => {
    setName('');
    setPartySize(1);
    setPhone('');
    setEmail('');
    setSpecialRequest('');
    setSelectedTime('');
    setSelectedDate(getTodayDate());
  };

  const workingHours = useMemo(() => {
    const raw =
      selectedRestaurant?.working_hours ||
      (selectedRestaurant as any)?.workingHours;

    if (!raw) {
      return null;
    }

    try {
      return typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch (error) {
      console.error("Invalid working hours JSON", error);
      return null;
    }
  }, [
    selectedRestaurant?.working_hours,
    (selectedRestaurant as any)?.workingHours
  ]);
  // Check selected date restaurant status
  useEffect(() => {

    if (!workingHours) {
      return;
    }


    setIsRestaurantClosed(
      checkIfRestaurantClosed(
        selectedDate,
        workingHours
      )
    );


  }, [
    selectedDate,
    workingHours
  ]);



  // Fetch slots based on restaurant timezone day
  useEffect(() => {


    const date = dayjs.tz(
      selectedDate,
      "YYYY-MM-DD",
      timezone
    );


    const days = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday"
    ];


    dispatch(
      fetchTimeSlotsRequest({
        day: days[date.day()]
      })
    );


  }, [
    selectedDate,
    timezone,
    dispatch
  ]);



  useEffect(() => {

    setShowSuccessPopup(false);
    setShowFailurePopup(false);

  }, []);



  useEffect(() => {

    if (
      reservationSuccess &&
      reservationData?.success
    ) {

      setShowSuccessPopup(true);
      setShowFailurePopup(false);

      window.scrollTo(0, 0);
    }

  }, [
    reservationSuccess,
    reservationData
  ]);



  useEffect(() => {

    if (
      reservationError
    ) {

      setShowFailurePopup(true);
      setShowSuccessPopup(false);

      window.scrollTo(0, 0);
    }

  }, [
    reservationError
  ]);


  // Apply timezone slot filtering
  useEffect(() => {


    if (isRestaurantClosed) {

      setTimeSlots([]);

      return;
    }



    if (!apiTimeSlots?.length) {

      setTimeSlots([]);

      return;
    }



   setTimeSlots(
  filterTimeSlots(
    apiTimeSlots,
    selectedDate,
    timezone
  )
);


  }, [
    apiTimeSlots,
    isRestaurantClosed,
    selectedDate,
    timezone
  ]);

  const getSlotStatus = (): SlotStatus => {

    if (tableLoading) {
      return "loading";
    }


    if (isRestaurantClosed) {
      return "closed";
    }


    if (!workingHours) {
      return "no-working-hours";
    }

    const isToday = selectedDate === getTodayDate();
    if (isToday && apiTimeSlots && apiTimeSlots.length > 0 && timeSlots.length === 0) {
      return "past";
    }


    if (timeSlots.length === 0) {
      return "no-slots";
    }


    return "available";
  };



  const slotStatus =
    getSlotStatus();




  const handleDateChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {


    const newDate =
      e.target.value;



    setSelectedDate(newDate);


    setSelectedTime("");



    if (workingHours) {

      setIsRestaurantClosed(
        checkIfRestaurantClosed(
          newDate,
          workingHours
        )
      );

    }

  };




  const handleBooking = (
    e: React.FormEvent<HTMLFormElement>
  ) => {


    e.preventDefault();



    dispatch(
      makeReservationRequest({

        name,

        phone,

        email,

        partySize,

        reservationDate: selectedDate,

        reservationTime: selectedTime,

        notes: specialRequest,

        occasion: '',

        source: 'website'

      })
    );


  };

  return (
    <>
      <div className="w-full bg-white min-h-screen" style={{ fontFamily: 'serif', paddingTop: `${navSpacing}px` }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="container mx-auto p-4 sm:p-8"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left side - Reservation Form */}
            <div className="bg-white dark:bg-[#4A5568] p-8 rounded-[1.5rem] shadow-xl border-2 border-gray-200 dark:border-gray-600 relative">
              <h1 className="font-bold text-4xl mb-6 text-gray-800 dark:text-gray-200">Make a Reservation</h1>

              <form onSubmit={handleBooking} className="space-y-6">
                {/* Name Field */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-lg bg-white text-gray-800 dark:text-gray-200 focus:ring-[#EA4243] focus:border-[#EA4243] focus:outline-none"
                    placeholder="Enter your name"
                    required
                  />
                </div>

                {/* Party Size and Date */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="partySize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                      <FaUsers className="inline-block mr-1 h-4 w-4" /> Party Size
                    </label>
                    <input
                      type="number"
                      id="partySize"
                      value={partySize}
                      onChange={(e) => setPartySize(Number(e.target.value))}
                      min="1"
                      max="50"
                      className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-lg bg-white text-gray-800 dark:text-gray-200 focus:ring-[#EA4243] focus:border-[#EA4243] focus:outline-none"
                      placeholder="Enter party size"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Date
                    </label>
                    <input
                      type="date"
                      id="date"
                      value={selectedDate}
                      min={getTodayDate()}
                      onChange={handleDateChange}
                      className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-lg bg-white text-gray-800 dark:text-gray-200 focus:ring-[#EA4243] focus:border-[#EA4243] focus:outline-none"
                    />
                  </div>
                </div>

                {/* Time Slots */}
                <div>
                  <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select a Time</p>
                  <TimeSlotSelector
                    loading={tableLoading}
                    status={slotStatus}
                    timeSlots={timeSlots}
                    selectedTime={selectedTime}
                    onTimeSelect={(time) => setSelectedTime(selectedTime === time ? '' : time)}
                    selectedDate={selectedDate}
                    errorMessage={timeSlotsError}
                  />
                </div>

                {/* Phone and Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-lg bg-white text-gray-800 dark:text-gray-200 focus:ring-[#EA4243] focus:border-[#EA4243] focus:outline-none"
                      placeholder="Your phone number"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-lg bg-white text-gray-800 dark:text-gray-200 focus:ring-[#EA4243] focus:border-[#EA4243] focus:outline-none"
                      placeholder="Your email"
                      required
                    />
                  </div>
                </div>

                {/* Special Request */}
                <div>
                  <label htmlFor="specialRequest" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Special Request <span className="text-gray-500 text-sm">(Optional)</span>
                  </label>
                  <textarea
                    id="specialRequest"
                    value={specialRequest}
                    onChange={(e) => setSpecialRequest(e.target.value)}
                    className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-lg bg-white text-gray-800 dark:text-gray-200 focus:ring-[#EA4243] focus:border-[#EA4243] focus:outline-none"
                    placeholder="Any special requests or notes"
                    rows={3}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={
                    reservationLoading ||
                    name.trim() === '' ||
                    partySize <= 0 ||
                    !selectedDate ||
                    selectedTime === '' ||
                    phone.trim() === '' ||
                    email.trim() === '' ||
                    isRestaurantClosed ||
                    timeSlots.length === 0
                  }
                  className={`w-full font-bold text-xl py-4 rounded-lg transition-opacity duration-200 shadow-lg ${reservationLoading ||
                    name.trim() === '' ||
                    partySize <= 0 ||
                    !selectedDate ||
                    selectedTime === '' ||
                    phone.trim() === '' ||
                    email.trim() === '' ||
                    isRestaurantClosed ||
                    (!tableLoading && timeSlots.length === 0)
                    ? 'bg-[#FF6347]/60 cursor-not-allowed'
                    : 'bg-[#FF6347] hover:opacity-90'
                    } text-white flex items-center justify-center`}
                >
                  {reservationLoading ? (
                    <>
                      <FaSpinner className="animate-spin mr-2 h-5 w-5" />
                      Processing...
                    </>
                  ) : (
                    'Book My Table!'
                  )}
                </button>
              </form>
            </div>

            {/* Right side - Restaurant Information */}
            <div className="space-y-8">
              <h2 className="font-bold text-4xl text-gray-800 dark:text-gray-200">Restaurant Information</h2>

              <div className="bg-white dark:bg-[#4A5568] p-8 rounded-[1.5rem] shadow-xl border-2 border-gray-200 dark:border-gray-600">
                <h3 className="font-bold text-2xl mb-6 text-gray-800 dark:text-gray-200">
                  Operating Hours
                </h3>

                <div className="space-y-4">
                  {!workingHours ? (
                    <div className="p-4 bg-gray-50 dark:bg-[#2D3748] rounded-lg text-center">
                      <p className="text-gray-500 dark:text-gray-400">
                        Hours not available
                      </p>
                    </div>
                  ) : (
                    (() => {

                      const formatTime12Hour = (time: string) => {
                        if (!time) return "";

                        const [hour, minute] = time.split(":");

                        const date = new Date();
                        date.setHours(Number(hour));
                        date.setMinutes(Number(minute));

                        return date.toLocaleTimeString([], {
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        });
                      };


                      const days = [
                        {
                          key: "sunday",
                          label: "Sunday"
                        },
                        {
                          key: "monday",
                          label: "Monday"
                        },
                        {
                          key: "tuesday",
                          label: "Tuesday"
                        },
                        {
                          key: "wednesday",
                          label: "Wednesday"
                        },
                        {
                          key: "thursday",
                          label: "Thursday"
                        },
                        {
                          key: "friday",
                          label: "Friday"
                        },
                        {
                          key: "saturday",
                          label: "Saturday"
                        }
                      ];


                      return days.map((day) => {

                        const hours = workingHours[day.key];

                        let text = "Closed";
                        let closed = true;


                        if (hours && !hours.isClosed) {

                          const slots = [];


                          if (
                            hours.firstHalf?.start &&
                            hours.firstHalf?.end
                          ) {
                            slots.push(
                              `${formatTime12Hour(hours.firstHalf.start)} - ${formatTime12Hour(hours.firstHalf.end)}`
                            );
                          }


                          if (
                            hours.secondHalf?.start &&
                            hours.secondHalf?.end
                          ) {
                            slots.push(
                              `${formatTime12Hour(hours.secondHalf.start)} - ${formatTime12Hour(hours.secondHalf.end)}`
                            );
                          }


                          if (slots.length > 0) {
                            text = slots.join(", ");
                            closed = false;
                          }

                        }


                        return (
                          <div
                            key={day.key}
                            className="flex justify-between items-center p-4 rounded-lg border-2 border-gray-200 dark:border-gray-600"
                          >

                            <span className="font-semibold text-lg text-gray-800 dark:text-gray-200">
                              {day.label}
                            </span>


                            <span
                              className={
                                closed
                                  ? "text-red-500 font-semibold"
                                  : "text-gray-600 dark:text-gray-300"
                              }
                            >
                              {text}
                            </span>

                          </div>
                        );

                      });

                    })()
                  )}
                </div>
              </div>

              {selectedRestaurant && (
                <div className="bg-white dark:bg-[#4A5568] p-8 rounded-[1.5rem] shadow-xl border-2 border-gray-200 dark:border-gray-600 relative">
                  <h3 className="font-bold text-2xl mb-4 text-gray-800 dark:text-gray-200">Find Us</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-2">{selectedRestaurant.address}</p>
                  {selectedRestaurant.phone && (
                    <p className="text-gray-600 dark:text-gray-300">{selectedRestaurant.phone}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Success Popup Modal */}
          <ReservationSuccessPopup
            isOpen={showSuccessPopup}
            message={
              reservationData?.message ||
              "Reservation successful"
            }
            isSuccess={true}
            onClose={() => {
              setShowSuccessPopup(false);
              dispatch(resetReservationState());
            }}
            onMakeAnother={() => {
              setShowSuccessPopup(false);
              dispatch(resetReservationState());
              resetForm();
            }}
          />


          <ReservationFailurePopup
            isOpen={showFailurePopup}
            message={
              typeof reservationError === "string"
                ? reservationError
                : "Reservation failed"
            }
            onClose={() => {
              setShowFailurePopup(false);
              dispatch(resetReservationState());
            }}
            onTryAgain={() => {
              setShowFailurePopup(false);
              dispatch(resetReservationState());
              setSelectedTime('');
            }}
          />
        </motion.div>
      </div>
    </>
  );
}
